import { Divider } from '@/components/Divider'
import { Locale } from '@/domain'
import { useTobiIntegration } from '@/lib/connectToFirestore'
import { useEnv } from '@/lib/env'
import { getData } from '@/lib/getJSON'
import { parseHostname, publicationInteractiveDomain } from '@/lib/parseHostname'
import styled from '@emotion/styled'
import { Municipality, MunicipalitySearch } from '@ta-interaktiv/react-municipality-search'
import IntlMessageFormat from 'intl-messageformat'
import * as t from 'io-ts'
import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import { font16Regular_, fontHeading3_ } from '@/materials/index'

interface Props extends React.ComponentPropsWithoutRef<'div'> {
  popularVoteId: string
}

const selectionHandler =
  (template: string) =>
  ({ GDENR }: Municipality) => {
    const parsedHostname = parseHostname()

    const msgf = new IntlMessageFormat(template, 'en')
    const href = msgf.format({
      publicationDomain: parsedHostname.publicationDomain,
      publicationInteractiveDomain: publicationInteractiveDomain(parsedHostname),
      bfsid: GDENR,
    }) as string

    changeRouteTo(href)
  }
// workaround for iOS Webview
const changeRouteTo = (newLocation: string) => {
  const link = window.document.createElement('a')
  link.setAttribute('href', newLocation)
  link.click()
}
const SpatialIO = t.type({ spatial_unit: t.string, spatial_date: t.string })

const SpatialReferenceIO = t.type({
  abstimmtag: t.string,
  schweiz: t.unknown,
  spatial_reference: t.array(SpatialIO),
  timestamp: t.string,
})

type SpatialReferenceType = t.TypeOf<typeof SpatialReferenceIO>

function useMunicipalityDataDate(popularVoteId: string) {
  const { firebaseApp } = useEnv()
  const [spacialDate, setSpacialDate] = React.useState<null | any>(null)
  React.useEffect(() => {
    const getSpacialDate = async () => {
      const [year, month, day] = popularVoteId.split('-') // 2021-09-26
      try {
        const result = await getData(
          firebaseApp,
          `https://app-prod-static-voteinfo.s3.eu-central-1.amazonaws.com/v1/ogd/sd-t-17-02-${year}${month}${day}-eidgAbstimmung.json`,
          {},
          SpatialReferenceIO,
        )
        const { spatial_reference } = result as SpatialReferenceType
        const [lastSpatialReference] = spatial_reference.slice(-1)
        if (lastSpatialReference) {
          const { spatial_date } = lastSpatialReference
          setSpacialDate(spatial_date)
        }
      } catch (e) {
        console.error(e)
      }
    }
    getSpacialDate()
  }, [firebaseApp, popularVoteId])
  return spacialDate
}

/**
 * This component is used at the top of the popular vote page.
 */
function TobiIntegration(props: Props) {
  const { intl } = useEnv()

  const { popularVoteId, ...rest } = props

  const tobiIntegration = useTobiIntegration({ popularVoteId })
  const municipalityDate = useMunicipalityDataDate(popularVoteId)

  // In case the tobi integration is not enabled or we don't know the date yet we don't show anything
  if (!tobiIntegration || !tobiIntegration.enabled || !municipalityDate) {
    return null
  }

  const [year, month, day] = municipalityDate.split('-')
  return (
    <Root {...rest}>
      <Divider variant="strong" />
      <Text>
        <FormattedMessage id="components.tobi-integration.label" defaultMessage="So hat meine Gemeinde abgestimmt" />
      </Text>
      <MunicipalitySearch
        showLastSelectedMunicipalities
        onSelectionHandler={selectionHandler(tobiIntegration.articleUrlTemplate)}
        municipalityData={`${year}${month}${day}_${intl.locale.split('-')[0] as Locale}`}
        locale={intl.locale.split('-')[0] as Locale}
      />
    </Root>
  )
}

export { TobiIntegration }

const Root = styled('div')`
  margin-bottom: 20px;

  & .municipality-search {
    max-width: initial;
    ${font16Regular_ as any};
  }
  & input {
    border: none;
    border-bottom: 1px solid var(--tx-sys-color-outline);
    border-radius: 0;

    color: var(--tx-sys-color-on-surface);
    background-color: var(--tx-sys-color-surface-container);
  }
  & .results {
    max-width: initial !important;
    position: static;
    border-radius: 0;
  }
  & .result {
    border-radius: 0;
  }
  & .result:hover,
  & .result:focus {
    box-shadow: var(--tx-sys-color-outline) 0px 0px 0px 1px inset;
  }
`

const Text = styled('div')`
  ${fontHeading3_ as any};
  margin-block: 20px;
`
